import React from 'react'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'

import { AudioPlayerContainer as Container, PlayerStatus, AppMetaData, formatTimeMS } from 'mytours-read-common'
import { Howl } from 'howler'
import { getUAParser } from '../../../../services/uaParser'

import {
	setCurrentSound,
	getCurrentSound,
	onBack,
	onForward,
	sendAnalyticsEvent,
	getAllSounds,
	DEFAULT_SKIP_SECONDS,
} from '../../../audio/soundController'
import { resolveAudioSource } from '../../../audio/audioSource'
import SyncedTranscript from '../../../audio/components/SyncedTranscript'
import { sendEvent, EventActionsEnum } from '../../../logging/analytics'
import { formatNumberStyle, getDirMode, waitForSomething } from '../../../platform'

import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	SlideshowSection,
	Spinner,
	Text,
	Ripple,
	Grid,
	GridRow,
	GridCol,
} from '@mytours/components'
import Modal from '../../../common/components/modal'
import strings from '../../../../strings'
import { cleanText } from '../../../common/functions'
import { BetaFeatures, FileRec } from 'mytours-read-api-typescript'
import { ImageSizePreset, ImageSizePresetMap } from '../../../../common_lib/types'
import { generateImageUrl } from '../../../../common_lib/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { updateTranscriptionHash } from '../../functions'

import '../styles/sectionMediaAudio.less'
import { formatPlayPauseLabel } from 'mobile_web/modules/audio/functions'
import { getSectionsQueryParams } from 'mobile_web/modules/navigation/queryParams'

interface HelmetData {
	title: string
	transcription: string
	file: FileRec
	imageUrl: string
	imageHeight: number | string
}

interface State {
	showTranscriptModal: boolean
	// This row's own track length in seconds, captured from the Howl metadata
	// preload (onload). `Infinity` until metadata resolves — the duration line
	// stays hidden until then.
	audioDuration: number
}

type Props = Container.Props & Container.Actions & { newAudioPlayerEnabled?: boolean }

class SectionMediaAudio extends React.Component<Props, State> {
	state = {
		showTranscriptModal: false,
		audioDuration: Infinity,
	}

	private positionInterval: NodeJS.Timeout | number
	private progressBreakpoints = {
		10: false,
		25: false,
		50: false,
		75: false,
	}
	private playButtonClicked = false
	private hash = ''
	private unListen: () => void

	componentDidMount = () => {
		const {
			container,
			audioPreview,
			coverImages,
			startTimes,
			fullScreenEnabled,
			collectionId,
			screen,
			slideshowEnabled,
		} = this.props

		this.props.loadAudioPreview(container, audioPreview, {
			coverImages,
			startTimes,
			fullScreenEnabled,
			collectionId,
			screen,
			slideshowEnabled,
		})
		this.createSound()

		this.unListen = ionHistory.listen(({ hash }) => {
			if (hash !== this.hash) {
				// TODO: this is temp fix: add global handle for hash changes
				if (hash === '' && this.hash === '#transcript' && this.state.showTranscriptModal) {
					this.toggleTranscriptModal(false)
				}

				this.hash = hash
			}
		})

		this.openAudioPlayerDependsOnQueryParams()
	}

	componentWillUnmount = () => {
		this.unListen()
	}

	private openAudioPlayerDependsOnQueryParams = () => {
		const { storySectionId, sectionItemId } = getSectionsQueryParams()

		if (!storySectionId || !sectionItemId) return

		const { audioPreview, container } = this.props
		const { file, id: audioId } = audioPreview
		const { id: fileId } = file

		if (sectionItemId !== audioId || storySectionId !== container?.id) return

		waitForSomething(() => getCurrentSound(audioId, container, fileId)).then(() => {
			// show audio bar to have access to fullscreen audio player
			this.props.setPlayerStatus(audioId, PlayerStatus.Pause, container, fileId)
		})
	}

	private createSound = async (): Promise<void> => {
		const { audioPreview, container } = this.props

		if (audioPreview?.file) {
			const { id: audioId } = audioPreview

			// firefox doesn't support html5 playing from howler
			// https://github.com/goldfire/howler.js/issues/1411
			const browser = getUAParser().getBrowser()
			const html5 = browser.name !== 'Firefox'

			const source = await resolveAudioSource(audioPreview)
			if (!source) {
				return
			}

			const { src, format, fileId } = source
			let isFirstStart = true

			const sound = new Howl({
				html5,
				format: [format],
				src,
				preload: 'metadata',
				onload: () => {
					this.props.audioLoaded(audioId, sound.duration(), container, fileId)
					// Capture this row's own length for the duration line (the global
					// currentAudio prop tracks whatever is *playing*, not this item).
					this.setState({ audioDuration: sound.duration() })
				},
				onloaderror: (_, error: unknown) => {
					this.props.loadAudioError(audioId, error, container, fileId)
				},
				onplay: () => {
					const { currentAudio, collection } = this.props

					if (isFirstStart) {
						sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioStart, { audioDuration: 0 })
					} else {
						sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioResume)
					}
					this.setPositionInterval()
					this.props.setPlayerStatus(audioId, PlayerStatus.Playing, container, fileId)
					isFirstStart = false
				},
				onpause: () => {
					const { currentAudio, collection } = this.props
					this.props.setPlayerStatus(audioId, PlayerStatus.Pause, container, fileId)
					sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioPause)
					this.removePositionInterval()
				},
				onstop: () => {
					this.removePositionInterval(true)
					this.props.setPlayerStatus(audioId, PlayerStatus.Stop, container, fileId)
					this.props.setCurrentTime(audioId, 0, container, fileId)
					// make play button clickable again
					this.playButtonClicked = false
				},
				onend: () => {
					const { currentAudio, collection } = this.props
					this.removePositionInterval(true)
					this.props.setAudioEnded(audioId, container, fileId)
					sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioComplete, {
						audioCurrentTime: -1,
						audioPercent: 100,
					})
					// make play button clickable again
					this.playButtonClicked = false
				},
			})

			setCurrentSound(audioId, container, fileId, sound)

			return
		}
	}

	// Stop the button's click from reaching the row and toggling playback twice.
	onPlayPause = (event: React.MouseEvent<HTMLElement>): void => {
		event.stopPropagation()
		this.togglePlayback()
	}

	togglePlayback = (): void => {
		// block double click on play button
		if (this.playButtonClicked) {
			return
		}

		this.playButtonClicked = true

		setTimeout(() => {
			this.playButtonClicked = false
		}, 500)

		const { audioPreview, currentAudio, container } = this.props

		const { playerStatus, audioTrack } = currentAudio || {}

		if (playerStatus === PlayerStatus.Loading && audioTrack?.audio) {
			return
		}

		const currentSound = getCurrentSound(audioPreview?.id, container, audioPreview?.file?.id)

		if (currentSound) {
			if (this.isCurrentAudio) {
				switch (playerStatus) {
					case PlayerStatus.Playing:
						currentSound.pause()
						return
					case PlayerStatus.Pause:
					case PlayerStatus.Ended:
						currentSound.play()
						return
				}
			} else {
				if (playerStatus === PlayerStatus.NotInit) {
					currentSound.play()
					return
				}
			}

			const sounds = getAllSounds()
			for (const sound of sounds) {
				sound.stop()
			}

			currentSound.play()
		}
	}

	onClickForward = (event: React.MouseEvent<HTMLElement>): void => {
		const { setCurrentTime, currentAudio, collection, container } = this.props
		const {
			duration,
			audioTrack: { audio },
		} = currentAudio || {}

		event.stopPropagation()

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipForward)
		onForward(audio?.id, duration, container, audio?.file?.id, DEFAULT_SKIP_SECONDS, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	onClickBack = (event: React.MouseEvent<HTMLElement>): void => {
		const { setCurrentTime, currentAudio, collection, container } = this.props
		const {
			audioTrack: { audio },
		} = currentAudio || {}

		event.stopPropagation()

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipBackward)
		onBack(audio?.id, container, audio?.file?.id, DEFAULT_SKIP_SECONDS, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	toggleTranscriptModal = (value?: React.MouseEvent | boolean): void => {
		if (typeof value !== 'boolean') {
			value.stopPropagation()
		}

		this.setState((prev: State) => {
			updateTranscriptionHash(value as unknown as boolean, prev.showTranscriptModal, () => {
				const { audioPreview, collection } = this.props

				sendEvent(EventActionsEnum.AudioViewTranscript, {
					audioTitle: audioPreview.name,
					collection: collection?.name,
					collectionId: collection?.id,
				})
			})

			return {
				showTranscriptModal: typeof value === 'boolean' ? value : !prev.showTranscriptModal,
			}
		})
	}

	get isCurrentAudio(): boolean {
		const { currentAudio, audioPreview, container: sectionContainer } = this.props

		const { audioTrack, container: currentAudioContainer } = currentAudio || {}

		const isCurrentAudio =
			(audioTrack?.audio &&
				audioPreview.id === audioTrack?.audio.id &&
				currentAudioContainer.id === sectionContainer.id &&
				currentAudioContainer.type === sectionContainer.type) ||
			false
		return isCurrentAudio
	}

	get isLoading(): boolean {
		const { currentAudio } = this.props
		const { playerStatus } = currentAudio || {}

		return playerStatus === PlayerStatus.Loading && this.isCurrentAudio
	}

	get isPlaying(): boolean {
		const { currentAudio } = this.props
		const { playerStatus } = currentAudio || {}

		return playerStatus === PlayerStatus.Playing && this.isCurrentAudio
	}

	renderTranscriptModal = (): JSX.Element => {
		const { audioPreview } = this.props
		const { showTranscriptModal } = this.state

		return (
			<Modal isOpen={showTranscriptModal} onDidDismiss={() => this.toggleTranscriptModal(false)}>
				<Card variant="ios" dir={getDirMode()} className="synced-transcript-modal-card">
					<CardHeader>
						<CardTitle as="h2" id="dialogTitle">
							{strings.transcript}
						</CardTitle>
						<Button
							variant="clear"
							className="close"
							onClick={() => this.toggleTranscriptModal(false)}
							aria-label={strings.close}
						>
							<i className="fal fa-xmark" />
						</Button>
					</CardHeader>
					<CardContent>
						<SyncedTranscript
							variant="modal"
							transcription={audioPreview.transcription}
							transcriptionFormat={audioPreview.transcription_format}
							mediaItemId={audioPreview.id}
							currentTime={this.isCurrentAudio ? (this.props.currentAudio?.currentTime ?? 0) : 0}
							isPlaying={this.isPlaying}
						/>
					</CardContent>
				</Card>
			</Modal>
		)
	}

	renderHelmet = ({ title, transcription, file, imageUrl, imageHeight }: HelmetData): JSX.Element => {
		const schema = {
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@type': 'AudioObject',
				caption: title,
				transcript: cleanText(transcription),
				contentSize: file && file.file_size,
				contentUrl: imageUrl,
				encodingFormat: file && file.content_type,
				height: imageHeight,
			}),
		}

		return <Helmet script={[schema]} />
	}

	renderAudioRow = (): JSX.Element => {
		const { audioPreview, newAudioPlayerEnabled } = this.props
		const { audioDuration } = this.state

		const { title, transcription, thumbnail_media_item: thumbnailMediaItem, file } = audioPreview

		// Audio length under the title — only with the new audio player enabled, and
		// only once the metadata preload has resolved a finite duration (sc-146618).
		const showDuration = !!newAudioPlayerEnabled && Number.isFinite(audioDuration)

		if (!file) {
			return null
		}

		// image
		let imageUrl: string | undefined
		if (thumbnailMediaItem?.file) {
			imageUrl = generateImageUrl(thumbnailMediaItem?.file, {
				imageSizePreset: ImageSizePreset.Grid,
			})
		}

		const helmet = this.renderHelmet({
			title,
			transcription,
			file,
			imageUrl,
			imageHeight: ImageSizePresetMap[ImageSizePreset.Grid].height,
		})

		// SlideshowSection renders the row; this component owns playback, transcripts and deep links.
		if (this.props.slideshowEnabled) {
			// SlideshowSection appends the title; full localization needs support for a complete label.
			const activateLabel = title ? strings.play : strings.play_media
			const pauseLabel = title ? strings.pause : strings.pause_media

			return (
				<>
					{helmet}
					{/* Use the app's themed outline colour and width instead of the package defaults. */}
					<div
						id={`section-item-id-${audioPreview.id}`}
						style={
							{
								'--sq-slideshow-section-now-playing-color': 'var(--audio-player-now-playing, transparent)',
								'--sq-slideshow-section-now-playing-width': 'var(--audio-player-now-playing-border-width, 3px)',
							} as React.CSSProperties
						}
					>
						{/* isPlaying also controls the outline, so the current track loses its outline when paused. */}
						<SlideshowSection
							dataCy="section-media-audio"
							title={title ?? ''}
							poster={imageUrl}
							posterAlt=""
							meta={
								showDuration
									? `${formatTimeMS(audioDuration)}${transcription ? ` | ${strings.transcript_available}` : ''}`
									: undefined
							}
							isPlaying={this.isPlaying}
							isLoading={this.isLoading}
							loadingLabel={strings.loading}
							onActivate={this.togglePlayback}
							activateLabel={activateLabel}
							pauseLabel={pauseLabel}
							hasTranscript={!!transcription}
							onTranscript={() => this.toggleTranscriptModal(true)}
							transcriptLabel={strings.transcript}
							dir={getDirMode()}
						/>
					</div>
					{this.renderTranscriptModal()}
				</>
			)
		}

		return (
			<>
				{helmet}

				{/* The name and the button role belong on the Button, not here: carrying them on the
				    row as well nested one interactive control inside another, which leaves the control
				    ambiguous to a screen reader. The handler stays on the row, so activating the button
				    by pointer or keyboard still bubbles up to it. */}
				<GridRow
					id={`section-item-id-${audioPreview.id}`}
					className={`audio-item ion-activatable${this.isCurrentAudio ? ' is-playing' : ''}`}
					onClick={this.onPlayPause}
					data-cy="section-media-audio-button"
				>
					<Button variant="clear" aria-label={formatPlayPauseLabel(this.isPlaying, title)}>
						<Grid>
							<GridRow>
								<GridCol
									className="thumbnail"
									style={{
										backgroundImage: imageUrl ? `url(${imageUrl})` : null,
									}}
								>
									<GridRow dir="ltr" className="audio-controls">
										<i className="fal fa-headphones headphones" />
										{this.isLoading ? (
											<Spinner variant="crescent" aria-label={strings.loading} />
										) : (
											<i className={`fas fa-${this.isPlaying ? 'pause' : 'play'}`} />
										)}
									</GridRow>
								</GridCol>

								<GridRow className="content" dir={getDirMode()}>
									<div className="text">
										<Text variant="inherit" className="title">
											{title}
										</Text>

										{showDuration && (
											<Text variant="inherit" className="audio-meta">
												{formatTimeMS(audioDuration)}
												{!!transcription && ` | ${strings.transcript_available}`}
											</Text>
										)}
									</div>

									{/* transcript */}
									{!!transcription && (
										<Button
											variant="clear"
											aria-label={strings.transcript}
											onClick={this.toggleTranscriptModal}
											className="transcript"
										>
											<i className="fal fa-align-left" />
										</Button>
									)}
								</GridRow>
								<Ripple trigger=".audio-item" />
							</GridRow>
						</Grid>
					</Button>
				</GridRow>

				{this.renderTranscriptModal()}
			</>
		)
	}

	renderAudioBig = (): JSX.Element => {
		const { audioPreview, height } = this.props
		const { thumbnail_media_item: thumbnailMediaItem, title, transcription, file } = audioPreview

		const isHeightTypeNumber = typeof height === 'number'

		let imageHeight: number | string
		if (isHeightTypeNumber) {
			imageHeight = height < 480 ? height : 480
		} else {
			imageHeight = height
		}
		const thumbnail = thumbnailMediaItem?.file
		const imageWidth = thumbnail && isHeightTypeNumber && thumbnail.width * (Number(height) / thumbnail.height) // keep image aspect ratio

		const imageUrl: string | undefined =
			thumbnail &&
			generateImageUrl(thumbnail, {
				imageSizePreset: ImageSizePreset.Row,
			})

		return (
			<>
				{this.renderHelmet({
					title,
					transcription,
					file,
					imageUrl,
					imageHeight,
				})}
				<Grid className="audio-big">
					<GridRow
						className="cover-image"
						style={{
							backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
							width: formatNumberStyle(imageWidth) || formatNumberStyle(imageHeight) || '100%',
							height: formatNumberStyle(imageHeight),
						}}
					>
						{this.isCurrentAudio ? (
							<GridRow className="controls">
								<GridCol>
									<Button
										variant="clear"
										className="time-controls-btn media-action-click"
										onClick={this.onClickBack}
										aria-label={
											strings.formatString(strings.accessibility_seek_backward, {
												seconds: DEFAULT_SKIP_SECONDS,
											}) as string
										}
									>
										<i
											className="fak fa-skip-back-10"
											aria-label={
												strings.formatString(strings.accessibility_seek_backward, {
													seconds: DEFAULT_SKIP_SECONDS,
												}) as string
											}
										/>
									</Button>
								</GridCol>
								<GridCol>
									{this.isLoading ? (
										<Spinner variant="crescent" aria-label={strings.loading} />
									) : (
										<Button
											variant="clear"
											className="media-action-click"
											onClick={this.onPlayPause}
											aria-label={formatPlayPauseLabel(this.isPlaying, title)}
											// `ariaLabel` won't change unless we remount
											key={formatPlayPauseLabel(this.isPlaying, title)}
										>
											<i className={`fas fa-${this.isPlaying ? 'pause' : 'play'}`} />
										</Button>
									)}
								</GridCol>
								<GridCol>
									<Button
										variant="clear"
										className="time-controls-btn media-action-click"
										onClick={this.onClickForward}
										aria-label={
											strings.formatString(strings.accessibility_seek_forward, {
												seconds: DEFAULT_SKIP_SECONDS,
											}) as string
										}
									>
										<i
											className="fak fa-skip-forward-10"
											aria-label={
												strings.formatString(strings.accessibility_seek_forward, {
													seconds: DEFAULT_SKIP_SECONDS,
												}) as string
											}
										/>
									</Button>
								</GridCol>
							</GridRow>
						) : (
							<Button
								variant="clear"
								onClick={this.onPlayPause}
								aria-label={formatPlayPauseLabel(this.isPlaying, title)}
								className="controls controls-small media-action-click"
							>
								{this.isLoading ? (
									<Spinner variant="crescent" aria-label={strings.loading} />
								) : (
									<i className={`fas fa-${this.isPlaying ? 'pause' : 'play'}`} />
								)}
							</Button>
						)}
					</GridRow>

					{title && (
						<GridRow className="audio-title-row">
							<i className="fal fa-headphones" />
							<Text variant="inherit" className="title">
								{title}
							</Text>
							{!!transcription && (
								<Button
									variant="clear"
									onClick={this.toggleTranscriptModal}
									className="transcript-button media-action-click"
									aria-label={strings.accessibility_open_transcript}
								>
									<i className="fal fa-align-left" />
								</Button>
							)}
						</GridRow>
					)}
				</Grid>
				{this.renderTranscriptModal()}
			</>
		)
	}

	render = () => {
		const { height } = this.props

		if (height !== undefined) {
			return this.renderAudioBig()
		}

		return this.renderAudioRow()
	}

	private sendAudioProgressEvents = () => {
		const { collection, container, currentAudio } = this.props
		const { audioTrack } = currentAudio || {}
		const { audio } = audioTrack || {}

		const sound = getCurrentSound(audio?.id, container, audio?.file?.id)

		if (!sound) {
			return
		}

		const duration = sound.duration()
		const currentTime = sound.seek()
		const percent = Math.round((100 * currentTime) / duration)

		if (percent >= 10 && percent < 25 && !this.progressBreakpoints[10]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 10 })
			this.progressBreakpoints[10] = true
		} else if (percent >= 25 && percent < 50 && !this.progressBreakpoints[25]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 25 })
			this.progressBreakpoints[25] = true
		} else if (percent >= 50 && percent < 75 && !this.progressBreakpoints[50]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 50 })
			this.progressBreakpoints[50] = true
		} else if (percent >= 75 && percent < 100 && !this.progressBreakpoints[75]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 75 })
			this.progressBreakpoints[75] = true
		}
	}

	private setPositionInterval = (): void => {
		this.removePositionInterval()

		this.positionInterval = setInterval(() => {
			const { container, audioPreview } = this.props

			// The sound can disappear between ticks (track switch / teardown race) —
			// getCurrentSound returns undefined then, and a throw here would repeat
			// every 500ms.
			const sound = getCurrentSound(audioPreview.id, container, audioPreview.file?.id)
			if (!sound) {
				return
			}

			this.props.setCurrentTime(audioPreview.id, sound.seek(), container, audioPreview.file?.id)
			this.sendAudioProgressEvents()
		}, 500)
	}

	private removePositionInterval = (resetBreakpoints?: boolean): void => {
		if (this.positionInterval) {
			clearInterval(this.positionInterval as number)
			if (resetBreakpoints) {
				this.progressBreakpoints = {
					10: false,
					25: false,
					50: false,
					75: false,
				}
			}
		}
	}
}

// Surface the new_audio_player beta flag so the duration line (sc-146618) only
// renders when the new audio player is enabled. Mirrors the selector used by
// AudioPlayerBar. Composed *inside* the audio Container so the container's
// state/dispatch props still pass through to the component alongside this flag.
const selectNewAudioPlayerEnabled = (state: { settings?: { appMetaData?: AppMetaData } }): boolean => {
	const betaFeatures = (state.settings?.appMetaData as { beta_features?: BetaFeatures } | undefined)?.beta_features
	return betaFeatures?.new_audio_player === true
}

const SectionMediaAudioComponent = Container.default(
	connect((state: any) => ({ newAudioPlayerEnabled: selectNewAudioPlayerEnabled(state) }))(SectionMediaAudio),
)
export default SectionMediaAudioComponent as any
